import type { WorkKind, WorkSide, WorkStatus } from './api'
import { formatDateShort } from './dates'

export const CUSTOMER_NAME = 'ÍGF'

export const WORK_KINDS: { key: WorkKind; label: string; hint: string; icon: string }[] = [
  { key: 'bilun', label: 'Bilun', hint: 'Eitthvað virkar ekki eða sýnir rangt.', icon: '⚠' },
  { key: 'smamunir', label: 'Smámunir', hint: 'Pirrandi smáatriði sem hægir á vinnunni.', icon: '✂' },
  { key: 'virkni', label: 'Ný virkni', hint: 'Eitthvað sem kerfið gerir ekki enn.', icon: '✦' },
  { key: 'utlit', label: 'Útlit', hint: 'Uppröðun, litir, orðalag.', icon: '◐' },
  { key: 'spurning', label: 'Spurning', hint: 'Ég skil ekki hvernig þetta á að virka.', icon: '?' },
]

export const WORK_STATUSES: { key: WorkStatus; label: string; bg: string; color: string; open: boolean }[] = [
  { key: 'ny', label: 'Ný', bg: 'var(--acc-blue-soft)', color: 'var(--acc-blue)', open: true },
  { key: 'i_skodun', label: 'Í skoðun', bg: 'var(--acc-amber-soft)', color: 'var(--acc-amber)', open: true },
  { key: 'samthykkt', label: 'Samþykkt', bg: 'var(--acc-green-soft)', color: 'var(--acc-green)', open: true },
  { key: 'i_vinnslu', label: 'Í vinnslu', bg: 'var(--acc-violet-soft)', color: 'var(--acc-violet)', open: true },
  { key: 'lokid', label: 'Lokið', bg: 'var(--brand)', color: 'var(--on-brand)', open: false },
  { key: 'hafnad', label: 'Hafnað', bg: 'var(--surface-2)', color: 'var(--muted)', open: false },
]

export const WORK_PRIORITIES: { key: 1 | 2 | 3; label: string; hint: string }[] = [
  { key: 1, label: 'Lágt', hint: 'Þegar tími gefst.' },
  { key: 2, label: 'Miðlungs', hint: 'Í næstu lotu.' },
  { key: 3, label: 'Hátt', hint: 'Stoppar vinnu núna.' },
]

export const TEAM_ONLY_STATUSES: WorkStatus[] = ['samthykkt', 'i_vinnslu', 'hafnad']

export const WORK_PLACES = [
  'Mælaborð',
  'Viðskiptavinir',
  'Fyrirtæki › Yfirlit',
  'Fyrirtæki › Staðsetningar',
  'Fyrirtæki › Kostnaður',
  'Fyrirtæki › Kolefnisspor',
  'Fyrirtæki › Grænt bókhald',
  'Fyrirtæki › Samanburður',
  'Fyrirtæki › Sækja gögn (Excel)',
  'Notendur',
  'Aðgangsumsóknir',
  'Innskráning',
  'Tölvupóstur frá kerfinu',
  'Stillingar',
]

export const kindMeta = (k: WorkKind) => WORK_KINDS.find((x) => x.key === k) ?? WORK_KINDS[0]
export const statusMeta = (s: WorkStatus) => WORK_STATUSES.find((x) => x.key === s) ?? WORK_STATUSES[0]
export const priorityMeta = (p: number) => WORK_PRIORITIES.find((x) => x.key === p) ?? WORK_PRIORITIES[1]

export const WORK_LIST_KEY = ['work', 'list'] as const
export const WORK_BADGE_KEY = ['work', 'badge'] as const
export const WORK_AGENT_KEY = ['work', 'agent'] as const
export const workItemKey = (number: number) => ['work', 'item', number] as const

export function fmtWhen(iso: string): string {
  const d = new Date(iso)
  const mins = Math.round((Date.now() - d.getTime()) / 60_000)
  if (mins < 1) return 'rétt í þessu'
  if (mins < 60) return `fyrir ${mins} mín`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `fyrir ${hours} klst`
  const days = Math.round(hours / 24)
  if (days < 7) return `fyrir ${days} ${days === 1 ? 'degi' : 'dögum'}`
  return formatDateShort(d)
}

const UPLOAD_LIMIT = 1024 * 1024
const UNREADABLE = 'Vafrinn gat ekki unnið myndina.'

function loadImage(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Þetta er ekki mynd sem vafrinn getur lesið.'))
    }
    img.src = url
  })
}

function encode(img: HTMLImageElement, side: number, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const scale = Math.min(1, side / Math.max(img.width, img.height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(img.width * scale))
    canvas.height = Math.max(1, Math.round(img.height * scale))
    const ctx = canvas.getContext('2d')
    if (!ctx) return reject(new Error(UNREADABLE))
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error(UNREADABLE))), 'image/jpeg', quality)
  })
}

export async function shrinkImage(file: Blob, maxSide = 1600): Promise<Blob> {
  const img = await loadImage(file)
  for (let side = maxSide; side >= 400; side = Math.round(side * 0.75)) {
    for (const quality of [0.85, 0.7, 0.55]) {
      const blob = await encode(img, side, quality)
      if (blob.size < UPLOAD_LIMIT) return blob
    }
  }
  throw new Error('Myndin er of stór, jafnvel eftir þjöppun.')
}

export function imagesFromClipboard(data: DataTransfer): File[] {
  return [...data.items]
    .filter((i) => i.type.startsWith('image/'))
    .map((i) => i.getAsFile())
    .filter((f): f is File => Boolean(f))
}

export const sideLabel = (s: WorkSide) => (s === 'team' ? 'Laufið' : CUSTOMER_NAME)
export const sideGenitive = (s: WorkSide) => (s === 'team' ? 'Laufsins' : CUSTOMER_NAME)
